<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="600">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>Edit Spot "{{ spot.label }}"</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue', false)"
                />
            </v-card-title>

            <v-container>
                <v-form v-model="formData.valid" class="mb-3" @submit.prevent="editSpot">
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required, vRules.maxLength(40)]"
                                          density="compact" label="Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['type']" :items="types" :rules="[vRules.required]"
                                      density="compact" item-title="name" item-value="id" label="Type"/>
                        </v-col>

                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer" :center="mapCenter" :options="{ maxBounds: bounds, maxBoundsViscosity: 0.95 }"
                                   :zoom="10" @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                                <l-polygon :lat-lngs="zoneLatLngArray" color="red"></l-polygon>
                                <!-- display all other spots in this zone -->
                                <!-- <template v-for="singleSport in sportLatLngArray">
                                    <l-polygon :lat-lngs="singleSport" color="yellow"></l-polygon>
                                </template>-->
                            </l-map>
                        </v-col>

                        <v-col class="py-0 text-right" cols="12">
                            <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-card>
    </v-dialog>
</template>

<script setup>
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/assets/css/leaflet.css";

require("leaflet-draw");
import L from "leaflet";
import {reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LPolygon, LTileLayer} from "@vue-leaflet/vue-leaflet";

const {showSnackbar} = useSnackbarStore();
const emits = defineEmits(["update:modelValue", "updateSpot"]);
const props = defineProps({
    modelValue: Boolean,
    spot: Object,
    zone: Object,
    types: Array,
});

const mapContainer = ref();
const errors = ref([]);
const mapCenter = ref([
    props.spot?.polygon_coordinates?.[0].lat ?? 47.41322,
    props.spot?.polygon_coordinates?.[0].lng ?? -1.219482,
]);
const dialogSwitch = ref(props.modelValue);
const formData = reactive({
    valid: false,
    name: props.spot.name ?? null,
    type: props.spot.type ?? null,
    polygon_coordinates:
        props.spot.polygon_coordinates?.map((coordinate) => {
            return {lat: coordinate.latitude, lng: coordinate.longitude};
        }) ?? [],
});

const zoneLatLngArray = props.zone.polygon_coordinates.map((cord) => ({
    lat: cord.latitude,
    lng: cord.longitude,
}));
const bounds = L.latLngBounds(zoneLatLngArray);

/*const sportLatLngArray = props.allSport.map((sport) =>
    sport.polygon_coordinates.map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
    }))
);*/

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    map.fitBounds(bounds);

    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: "#9d1212", // Color the shape will turn when intersects
                    message: "<strong>Oh snap!<strong> you can't draw that!", // Message that will show when intersect
                },
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems,
        },
    });
    map.addControl(drawControl);

    const polygon = L.polygon(formData.polygon_coordinates, {
        // editable: true,
    });
    drawnItems.addLayer(polygon);
    mapCenter.value = polygon.getBounds()["_northEast"];

    map.on("draw:created", function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        if (e.layerType === "polygon") {
            formData["location_changed"] = 1;
            formData["polygon_coordinates"] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on("draw:edited", function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData["location_changed"] = 1;
                formData["polygon_coordinates"] = layer.getLatLngs()[0];
            }
        });
    });

    map.on("draw:deleted", function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData["location_changed"] = 1;
        formData["polygon_coordinates"] = null;
    });
}

const isPointInsidePolygon = (zone, spot) => {
    const zones = zone.map((item) => ([item.lat, item.lng]));
    const spots = spot.map((item) => ([item.lat, item.lng]));
    return polygonInPolygon(zones, spots);
}
const polygonInPolygon = (outerPolygon, innerPolygon) => {
    for (let i = 0; i < innerPolygon.length; i++) {
        if (!pointInPolygon(outerPolygon, innerPolygon[i])) {
            return false;
        }
    }
    return true;
}
const pointInPolygon = (polygon, point) => {
    let inside = false;
    const x = parseFloat(point[0]);
    const y = parseFloat(point[1]);

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = parseFloat(polygon[i][0]);
        const yi = parseFloat(polygon[i][1]);
        const xj = parseFloat(polygon[j][0]);
        const yj = parseFloat(polygon[j][1]);

        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

        if (intersect) {
            inside = !inside;
        }
    }
    return inside;
}

const editSpot = () => {
    errors.value = [];
    const isInside = isPointInsidePolygon(zoneLatLngArray, formData.polygon_coordinates)
    if (!isInside) {
        errors.value.push("New sport must be created inside the zone.");
    } else {
        if (formData.valid) {
            send("put", `companies/locations/zones/${props.zone.id}/spots/${props.spot.id}`, formData)
                .then((response) => {
                    showSnackbar(response.data.message);
                    if (response.data.success) {
                        emits("updateSpot", response.data.data);
                        emits("update:modelValue", false);
                    }
                })
                .catch(function (errorResponse) {
                    if (errorResponse.response.data.errors) {
                        let validationErrors = errorResponse.response.data.errors;
                        Object.keys(validationErrors).forEach((key) => {
                            errors.value.push(validationErrors[key][0]);
                        });
                    } else if (errorResponse.response.data.message) {
                        errors.value.push(errorResponse.response.data.message);
                    }
                    showSnackbar("Something went wrong", "error");
                    document
                        .getElementById("heading")
                        .scrollIntoView({behavior: "smooth"});
                });
        }
    }
};
</script>
